// src/components/resumo/annotations/useHighlight.ts
// 🎯 Hook para grifo em modo contínuo

import { useCallback, useEffect, useState, useRef } from 'react';
import { Highlight, HighlightColor } from './annotations.types';
import { updateHighlightCursor, updateRemoveCursor, clearDynamicCursor } from './dynamicCursors';
import { selectionCrossesHighlight } from './highlightUtils';
import { getAbsoluteOffsetFromRange } from './rangeOffset';
import { captureAnchor } from './blockAnchor';

// ========== MOBILE FLOATING BUTTON HELPERS ==========
let mobileFloatingBtnEl: HTMLElement | null = null;

const removeMobileFloatingBtn = () => {
    if (mobileFloatingBtnEl) {
        mobileFloatingBtnEl.remove();
        mobileFloatingBtnEl = null;
    }
};

const showMobileFloatingBtn = (
    selection: Selection,
    range: Range,
    color: HighlightColor,
    thickness: 'thin' | 'medium' | 'thick',
    style: 'solid' | 'underline' | 'translucent'
) => {
    removeMobileFloatingBtn();
    document.querySelectorAll('.mobile-highlight-floating-btn').forEach(el => el.remove());

    const rect = range.getBoundingClientRect();
    if (!rect.width) return;

    const btn = document.createElement('div');
    btn.className = 'mobile-highlight-floating-btn';
    btn.innerHTML = `
        <button class="mhfb-highlight-btn" data-action="highlight">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Grifar
        </button>
        <button class="mhfb-cancel-btn" data-action="cancel">✕</button>
    `;

    // Posicionar acima da seleção
    const top = rect.top + window.scrollY - 52;
    const left = Math.min(
        rect.left + rect.width / 2 - 60,
        window.innerWidth - 136
    );

    btn.style.cssText = `
        position: absolute;
        top: ${Math.max(top, window.scrollY + 8)}px;
        left: ${Math.max(left, 8)}px;
        z-index: 99999;
    `;

    document.body.appendChild(btn);
    mobileFloatingBtnEl = btn;

    // Capturar seleção antes de qualquer toque no botão
    const savedSelectionText = selection.toString().trim();
    const savedRange = range.cloneRange();

    btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const target = e.target as HTMLElement;
        const action = target.closest('[data-action]')?.getAttribute('data-action');

        if (action === 'highlight') {
            // Restaurar seleção e aplicar grifo
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                sel.addRange(savedRange);
                // chamar callback externo via evento customizado
                document.dispatchEvent(new CustomEvent('mobile-apply-highlight', {
                    detail: { range: savedRange, text: savedSelectionText }
                }));
            }
        }
        removeMobileFloatingBtn();
        const sel = window.getSelection();
        if (sel) sel.removeAllRanges();
    });
};

export const useHighlight = (
    onAddHighlight: (highlight: Highlight) => void,
    onRemoveHighlight: (id: string) => void,
    pageId: string,
    onDirtyChange?: (isDirty: boolean) => void
) => {
    const [isHighlighting, setIsHighlighting] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [currentColor, setCurrentColor] = useState<HighlightColor>('yellow');
    const [currentThickness, setCurrentThickness] = useState<'thin' | 'medium' | 'thick'>('medium');
    const [currentStyle, setCurrentStyle] = useState<'solid' | 'underline' | 'translucent'>('solid');
    const lastSelectionRef = useRef<string | null>(null);
    const lastClickTimeRef = useRef<number>(0);

    // ✅ Aplicar grifo no DOM
    const applyHighlight = useCallback((
        selection: Selection,
        color: HighlightColor,
        thickness: 'thin' | 'medium' | 'thick',
        style: 'solid' | 'underline' | 'translucent'
    ) => {
        try {
            const range = selection.getRangeAt(0);
            const selectedText = selection.toString().trim();

            if (!selectedText) return;

            const highlightId = `highlight_${Date.now()}_${Math.random()}`;

            // ✅ CRÍTICO: Calcular offset absoluto usando Range (SOLUÇÃO CORRETA)
            const contentElement = document.querySelector('.rrp-rendered-content') as HTMLElement;
            if (!contentElement) return;

            // ✅ NOVO: Usar TreeWalker para calcular offset exato
            const { absoluteStart, absoluteEnd } = getAbsoluteOffsetFromRange(contentElement, range);

            // ✅ Validar se o offset é válido
            if (absoluteStart < 0 || absoluteEnd <= absoluteStart) {
                console.error('❌ Offset inválido:', { absoluteStart, absoluteEnd });
                selection.removeAllRanges();
                return;
            }

            console.log('📍 Offset absoluto:', { absoluteStart, absoluteEnd, text: selectedText });

            const anchor = captureAnchor(range, contentElement, absoluteStart, absoluteEnd);

            // ✅ Criar objeto Highlight (para persistência)
            const highlight: Highlight = {
                id: highlightId,
                pageId,
                startOffset: absoluteStart,
                endOffset: absoluteEnd,
                ...anchor,
                color,
                thickness,
                style,
                text: selectedText,
                createdAt: new Date().toISOString()
            };

            // ✅ Adicionar ao estado (React vai renderizar)
            onAddHighlight(highlight);

            // ✅ ADICIONAR:
            if (onDirtyChange) {
                onDirtyChange(true);
            }

            // ✅ Limpar seleção (dupla limpeza)
            selection.removeAllRanges();
            setTimeout(() => {
                const sel = window.getSelection();
                if (sel) sel.removeAllRanges();
                lastSelectionRef.current = null;
            }, 0);

            console.log('✅ Grifo aplicado:', highlightId);
        } catch (error) {
            console.error('❌ Erro ao aplicar grifo:', error);
        }
    }, [onAddHighlight, pageId, onDirtyChange]);

    useEffect(() => {
        if (!isHighlighting) {
            document.body.classList.remove('highlight-mode');
            lastSelectionRef.current = null;
            lastClickTimeRef.current = 0;
            // ✅ Remover botão flutuante mobile se existir
            removeMobileFloatingBtn();
            return;
        }

        document.body.classList.add('highlight-mode');
        updateHighlightCursor(currentColor);

        const isMobile = () => window.matchMedia('(pointer: coarse)').matches;

        // ========== DESKTOP: comportamento original ==========
        const handleMouseUp = (e: MouseEvent) => {
            if (isMobile()) return; // mobile usa outro fluxo

            const now = Date.now();
            const timeSinceLastClick = now - lastClickTimeRef.current;
            if (timeSinceLastClick < 300) {
                const selection = window.getSelection();
                if (selection) selection.removeAllRanges();
                return;
            }
            lastClickTimeRef.current = now;

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;
            const selectedText = selection.toString().trim();
            if (!selectedText) return;
            if (selectedText.length > 5000) { selection.removeAllRanges(); return; }
            if (lastSelectionRef.current === selectedText) { selection.removeAllRanges(); return; }
            lastSelectionRef.current = selectedText;

            const range = selection.getRangeAt(0);
            const contentElement = document.querySelector('.rrp-rendered-content');
            if (!contentElement?.contains(range.commonAncestorContainer)) {
                lastSelectionRef.current = null;
                return;
            }
            if (selectionCrossesHighlight(selection)) {
                selection.removeAllRanges();
                lastSelectionRef.current = null;
                return;
            }

            applyHighlight(selection, currentColor, currentThickness, currentStyle);
        };

        // ========== MOBILE: selectionchange + botão flutuante ==========
        let mobileDebounceTimer: ReturnType<typeof setTimeout> | null = null;

        const handleSelectionChange = () => {
            if (!isMobile()) return;

            if (mobileDebounceTimer) clearTimeout(mobileDebounceTimer);

            mobileDebounceTimer = setTimeout(() => {
                const selection = window.getSelection();
                const selectedText = selection?.toString().trim() || '';

                if (!selectedText || selectedText.length < 2) {
                    removeMobileFloatingBtn();
                    return;
                }

                const range = selection!.getRangeAt(0);
                const contentElement = document.querySelector('.rrp-rendered-content');
                if (!contentElement?.contains(range.commonAncestorContainer)) {
                    removeMobileFloatingBtn();
                    return;
                }

                if (selectionCrossesHighlight(selection!)) {
                    removeMobileFloatingBtn();
                    return;
                }

                showMobileFloatingBtn(selection!, range, currentColor, currentThickness, currentStyle);
            }, 400); // debounce: espera usuário terminar de ajustar seleção
        };

        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('selectionchange', handleSelectionChange);

        // ========== MOBILE: aplicar grifo via evento customizado ==========
        const handleMobileApply = (e: Event) => {
            const customE = e as CustomEvent;
            const { range } = customE.detail;

            const sel = window.getSelection();
            if (!sel) return;
            sel.removeAllRanges();
            sel.addRange(range);

            applyHighlight(sel, currentColor, currentThickness, currentStyle);
        };

        document.addEventListener('mobile-apply-highlight', handleMobileApply);


        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('selectionchange', handleSelectionChange);
            document.removeEventListener('mobile-apply-highlight', handleMobileApply);
            if (mobileDebounceTimer) clearTimeout(mobileDebounceTimer);
            document.body.classList.remove('highlight-mode');
            clearDynamicCursor();
            removeMobileFloatingBtn();
        };
    }, [isHighlighting, currentColor, currentThickness, currentStyle, applyHighlight]);

    // ✅ Remover grifo (React vai re-renderizar)
    const removeHighlight = useCallback((id: string) => {
        console.log('🗑️ Removendo grifo:', id);
        // Não precisa mexer no DOM - o React vai re-renderizar sem este grifo
    }, []);

    // ✅ MODO REMOVER: Remover grifo ao clicar
    useEffect(() => {
        if (!isRemoving) {
            document.body.classList.remove('remove-mode');
            return;
        }

        document.body.classList.add('remove-mode');
        updateRemoveCursor();

        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const highlightEl = target.closest('[data-highlight-id]') as HTMLElement;

            if (highlightEl) {
                const highlightId = highlightEl.dataset.highlightId;
                if (highlightId) {
                    removeHighlight(highlightId);
                    onRemoveHighlight(highlightId);


                    // ✅ ADICIONAR:
                    if (onDirtyChange) {
                        onDirtyChange(true);
                    }
                }
            }
        };

        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
            document.body.classList.remove('remove-mode');
            clearDynamicCursor();
        };
    }, [isRemoving, onRemoveHighlight, removeHighlight, onDirtyChange]);


    return {
        isHighlighting,
        setIsHighlighting,
        isRemoving,
        setIsRemoving,
        currentColor,
        setCurrentColor,
        currentThickness,
        setCurrentThickness,
        currentStyle,
        setCurrentStyle,
        removeHighlight
    };
};